import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api, { adminAPI, organizerAPI } from '../../services/api';
import { isInvalidPhotoUrl } from '../../utils/photoUtils';
import { S } from '../staff/StaffUI';
import { DiamondMark } from '../../theme/luxe';

// Luxe fonts — same Google Fonts the member portal loads (theme/luxe.js), plus
// Noto Sans SC for Chinese body text. Mounted as its own <style> so the
// @import sits first in its sheet (it would be ignored mid-bundle in CSS).
const LUXE_FONTS_CSS = "@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=Jost:wght@300;400;500&family=Noto+Serif+SC:wght@400;500&family=Noto+Sans+SC:wght@300;400;500&display=swap');";
import {
  Heart,
  LayoutDashboard,
  Users,
  FolderSearch,
  Sparkles,
  MessageCircle,
  CheckSquare,
  Calendar,
  Gift,
  CreditCard,
  DollarSign,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  UserCircle,
  FileText,
  Activity,
  Layers,
  TrendingUp,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
  Link2,
  Globe,
  Database,
  FileCheck,
  Wallet,
  MessageSquare,
  Zap,
  AlertTriangle,
  ClipboardList,
  Star,
  ArrowUpRight,
  Code,
  Video,
  Phone,
  HelpCircle,
  Bug,
  Eraser,
  GraduationCap,
  Target,
  // Icons added to differentiate menu items that previously shared a
  // base icon. Names trimmed to ones the installed lucide-react actually
  // exports — e.g. FileEdit instead of FilePen, Heart-with-suffix forms
  // aren't all present so we fall back to plain alternatives where needed.
  Receipt,
  FileEdit,
  ListChecks,
  Package,
  Users2,
  BadgeDollarSign,
  CalendarDays,
  CalendarPlus,
  Sprout,
  ScanLine,
  Banknote,
  ShieldCheck,
  CalendarCheck,
  CalendarClock,
} from 'lucide-react';
import { isSupervisorUser as isServiceSupervisorUser } from '../service/sopShared';
import { useTour } from '../tour/tourCore';

/**
 * Hook: enables reliable horizontal touch-scrolling on mobile for table wrappers.
 * Observes the DOM for scrollable containers and attaches touch handlers
 * that detect horizontal swipes and programmatically scroll the container.
 */
const useMobileTableScroll = () => {
  useEffect(() => {
    // Only run on touch devices
    if (!('ontouchstart' in window)) return;

    const THRESHOLD = 12; // px – minimum move before locking direction (large enough to ignore tap jitter)
    const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, label, [role="button"], [onclick]';

    const attach = (el) => {
      // Skip if already attached
      if (el._hscroll) return;
      // Only attach if the element actually scrolls horizontally
      if (el.scrollWidth <= el.clientWidth) return;

      let startX = 0, startY = 0, scrollL = 0, locked = null;

      const onStart = (e) => {
        // Re-check scrollability (content may have changed)
        if (el.scrollWidth <= el.clientWidth) { locked = 'skip'; return; }
        // If the touch starts on an interactive element, don't take over —
        // let the native click/tap fire without our preventDefault interfering.
        if (e.target.closest(INTERACTIVE_SELECTOR)) { locked = 'skip'; return; }
        const t = e.touches[0];
        startX = t.pageX;
        startY = t.pageY;
        scrollL = el.scrollLeft;
        locked = null;
      };

      const onMove = (e) => {
        if (locked === 'v' || locked === 'skip') return;
        const t = e.touches[0];
        const dx = startX - t.pageX;
        const dy = startY - t.pageY;

        if (locked === null) {
          if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
          // Lock direction based on dominant axis
          locked = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
          if (locked === 'v') return;
        }

        // Horizontal scroll
        e.preventDefault();
        el.scrollLeft = scrollL + dx;
      };

      const onEnd = () => { locked = null; };

      el.addEventListener('touchstart', onStart, { passive: true });
      el.addEventListener('touchmove', onMove, { passive: false });
      el.addEventListener('touchend', onEnd, { passive: true });
      el._hscroll = { onStart, onMove, onEnd };
    };

    const detach = (el) => {
      if (!el._hscroll) return;
      el.removeEventListener('touchstart', el._hscroll.onStart);
      el.removeEventListener('touchmove', el._hscroll.onMove);
      el.removeEventListener('touchend', el._hscroll.onEnd);
      delete el._hscroll;
    };

    const scan = () => {
      document.querySelectorAll(
        '.table-wrapper, .table-container, .table-responsive, [style*="overflow-x"]'
      ).forEach(attach);
    };

    // Initial scan
    scan();

    // Observe DOM for dynamically added tables
    const observer = new MutationObserver(() => scan());
    observer.observe(document.body, { childList: true, subtree: true });

    // Re-scan on resize (scrollWidth may change)
    window.addEventListener('resize', scan);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scan);
      document.querySelectorAll(
        '.table-wrapper, .table-container, .table-responsive, [style*="overflow-x"]'
      ).forEach(detach);
    };
  }, []);
};

const getPhotoUrl = (url) => {
  if (isInvalidPhotoUrl(url)) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url.includes('_s.') ? url.replace('_s.', '_b.') : url;
  }
  // New-system photos stored under /uploads/ → serve from current origin
  if (url.startsWith('/uploads/') || url.startsWith('/up/')) {
    return url;
  }
  const bigUrl = url.includes('_s.') ? url.replace('_s.', '_b.') : url;
  return 'https://match2.findu.love/up/' + bigUrl;
};

// Chinese labels for the global search results (the allPages list uses English
// names; this maps each path to its zh label so search reads in Chinese).
const PAGE_NAME_ZH = {
  '/': '工作台', '/profiles': '档案', '/profiles/new': '新增档案', '/directory': '目录',
  '/matches': '匹配', '/dating-schedules': '约会安排', '/connections': '连接',
  '/messages': '消息', '/notifications': '通知', '/tasks': '任务', '/calendar': '日历',
  '/service-supervision': '服务监督', '/interview-bookings': '面谈预约', '/interview-availability': '可面谈时间', '/affiliate': '推广计划', '/billing': '账单', '/settings': '设置',
  '/admin': '管理后台', '/admin/users': '用户管理', '/admin/roles': '角色管理', '/admin/profiles': '所有档案',
  '/admin/custom-fields': '自定义字段', '/admin/coaches': '教练管理', '/admin/dating-schedules': '约会安排（后台）',
  '/admin/matches': '后台匹配', '/admin/trends': '趋势管理', '/admin/billing': '后台账单',
  '/admin/affiliates': '推广管理', '/admin/plans': '套餐管理', '/admin/auto-responses': '自动回复管理',
  '/admin/settings': '平台设置', '/admin/translations': '翻译', '/admin/database-sync': '数据库同步',
  '/admin/logs': '操作日志', '/admin/contracts': '已签文件',
  '/training': '培训', '/admin/training': '培训管理',
};

const DashboardLayout = () => {
  const { user, logout, hasRole, hasPermission } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const isZh = language === 'zh';
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // For mobile
  // "Drawer mode" matches the global CSS breakpoint (index.css hides the
  // sidebar off-canvas at <=992px), so tablets also get the header + drawer
  // button. `isPhone` (<768px) additionally shows the bottom tab bar.
  const isMobileInitial = window.innerWidth <= 992;
  const [isPhone, setIsPhone] = useState(window.innerWidth < 768);
  // Default to collapsed on desktop (user expands explicitly), but always
  // expanded on mobile so the off-canvas drawer shows icon + label, never
  // icon-only. The desktop default is overridable via the admin Theme setting
  // (设置 → 偏好 → 主题 → 左侧菜单默认隐藏).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (isMobileInitial) return false;
    try {
      const th = JSON.parse(localStorage.getItem('mm_admin_theme') || '{}');
      if (typeof th.sidebarCollapsedDefault === 'boolean') return th.sidebarCollapsedDefault;
    } catch (_) { /* ignore */ }
    return true; // legacy default: collapsed on desktop
  });
  const [isMobile, setIsMobile] = useState(isMobileInitial);

  // Hover-to-reveal label for collapsed-sidebar nav items. We render ONE
  // shared <div> with position: fixed (which escapes the sidebar's overflow
  // clip) instead of per-item tooltips inside the scroll container. Each
  // nav-item-wrapper sets/clears `collapsedTooltip` on enter/leave.
  const [collapsedTooltip, setCollapsedTooltip] = useState(null); // { text, top } | null
  const showCollapsedTooltip = (text, anchorEl) => {
    if (!sidebarCollapsed || !text || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setCollapsedTooltip({ text, top: rect.top + rect.height / 2 });
  };
  const hideCollapsedTooltip = () => setCollapsedTooltip(null);

  // 清除缓存 — clears backend in-memory caches + browser HTTP cache (server
  // Clear-Site-Data header) AND frontend session/local data caches + Cache
  // Storage + service workers, then reloads so /admin/* pages refetch fresh.
  const [clearingCache, setClearingCache] = useState(false);
  const handleClearCache = async () => {
    if (clearingCache) return;
    if (!window.confirm(isZh ? '确定清除前端和后端缓存？页面将自动刷新。' : 'Clear frontend & backend cache? The page will reload.')) return;
    setClearingCache(true);
    try {
      await api.post('/admin/clear-cache').catch(() => {});
      const keep = new Set(['token', 'mm_admin_theme', '_region_china']);
      try { Object.keys(localStorage).forEach(k => { if (!keep.has(k)) localStorage.removeItem(k); }); } catch (_) { /* */ }
      try { sessionStorage.clear(); } catch (_) { /* */ }
      try { if (window.caches) { const ks = await caches.keys(); await Promise.all(ks.map(k => caches.delete(k))); } } catch (_) { /* */ }
      try { if (navigator.serviceWorker) { const rs = await navigator.serviceWorker.getRegistrations(); await Promise.all(rs.map(r => r.unregister())); } } catch (_) { /* */ }
    } finally {
      setTimeout(() => window.location.reload(true), 400);
    }
  };

  // If the sidebar gets re-expanded while a tooltip is showing, drop it so
  // the orphaned label doesn't sit floating on the page.
  useEffect(() => { if (!sidebarCollapsed) setCollapsedTooltip(null); }, [sidebarCollapsed]);

  // Logout handler: redirect admin/coach to /admin/login
  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  // Custom touch-scroll hook disabled — relies on native overflow-x: auto
  // and -webkit-overflow-scrolling: touch instead. The custom handler was
  // suppressing synthesized click events on buttons inside scrollable tables.
  // useMobileTableScroll();

  // Badge counts state
  const [badgeCounts, setBadgeCounts] = useState({
    matches: 0,      // Match requests received
    messages: 0,     // Unread messages
    tasks: 0,        // Tasks due soon
    calendar: 0,     // Events due today
    billing: 0,      // Balance due
    notifications: 0, // Unread notifications
    assignmentRequests: 0, // Received assignment requests
    pendingApprovals: 0, // Pending KPI approvals (supervisors only)
    supervision: 0,  // 服务监督: records waiting for a 主管 (contacts, frequency requests, monthly reports, 整改 to verify)
    kpi: 0           // Coach / leader-coach unread KPI items (active warnings)
  });
  
  // Browser push for STAFF. Until now only the client portal ever subscribed,
  // so every coach reminder could reach the in-app bell and nowhere else.
  // ensurePushSubscription() asks at most once and no-ops where push cannot
  // work (unsupported browser, permission denied, or a viewer in China, where
  // FCM is blocked).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { registerServiceWorker, ensurePushSubscription } = await import('../../utils/pushNotifications');
        await registerServiceWorker();
        setTimeout(() => { if (!cancelled) ensurePushSubscription({ staff: true }); }, 4000);
      } catch (e) { /* push is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Check if user is coach
  const isCoachUser = user?.role?.toLowerCase() === 'coach';

  // Check if user is leader coach
  const isLeaderCoachUser = user?.role?.toLowerCase() === 'leader_coach';

  // Check if user is supervisor (supervisor or supervisor_coach)
  const isSupervisorUser = user?.role?.toLowerCase() === 'supervisor_coach' || user?.role?.toLowerCase() === 'supervisor';

  // Interviewer is an *additional* role assigned via user_roles; check both the
  // primary role and the secondary roles array exposed by /auth.
  const _userRolesArr = Array.isArray(user?.roles) ? user.roles.map(r => (r || '').toLowerCase()) : [];
  const isInterviewerUser = user?.role?.toLowerCase() === 'interviewer' || _userRolesArr.includes('interviewer');
  const isMatchmakerUser = user?.role?.toLowerCase() === 'matchmaker' || _userRolesArr.includes('matchmaker');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  
  // Refresh the sidebar badge counts on mount and periodically.
  // On the /profiles list we skip the polling so that heavy page stays light —
  // re-runs on navigation so polling resumes when leaving /profiles.
  // (The header notification bell — and its own /notifications + KPI polling —
  // was removed; nothing in the header reads that data any more.)
  useEffect(() => {
    if ((location.pathname || '').startsWith('/profiles')) {
      return;
    }
    fetchBadgeCounts();
    const badgeInterval = setInterval(fetchBadgeCounts, 30000); // Refresh badges every 30 seconds

    // Cross-page bridge: any page can `window.dispatchEvent(new
    // Event('matchmaker:badge-refresh'))` to force a badge refetch.
    // `/messages` fires this after marking a conversation as read so the
    // header badge drops to 0 right away instead of lagging up to 30s
    // behind the next poll. Idempotent; layout consumes events itself.
    const onBadgeRefresh = () => fetchBadgeCounts();
    window.addEventListener('matchmaker:badge-refresh', onBadgeRefresh);

    return () => {
      clearInterval(badgeInterval);
      window.removeEventListener('matchmaker:badge-refresh', onBadgeRefresh);
    };
  }, [location.pathname]);

  // Refetch badges whenever the route changes. Catches the common pattern of
  // "user reads inbox on /messages, then navigates elsewhere" — without this
  // the unread badge stays stale until the next 30s poll fires.
  useEffect(() => {
    fetchBadgeCounts();
  }, [location.pathname]); // eslint-disable-line

  // Toggle the global admin-mobile-shell body class so the per-route
  // /styles/admin-mobile.css overlay applies on /admin/* pages only.
  // Removed on cleanup so client / coach pages aren't affected when navigating away.
  useEffect(() => {
    const isAdmin = (location.pathname || '').startsWith('/admin');
    if (isAdmin) {
      document.body.classList.add('admin-mobile-shell');
    } else {
      document.body.classList.remove('admin-mobile-shell');
    }
    return () => { document.body.classList.remove('admin-mobile-shell'); };
  }, [location.pathname]);

  // Handle mobile responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      setIsPhone(window.innerWidth < 768);
      // On mobile, always keep sidebar expanded (not collapsed)
      if (mobile) {
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch badge counts for sidebar
  const fetchBadgeCounts = async () => {
    try {
      // The backend sets `role_name` (joined from the roles table) and only
      // sometimes `role`, so normalise both here. Older code that read just
      // `user.role` silently broke for matchmakers — they have a `role_name`
      // but no `role`, so the unread badge never fired for them.
      const _userRole = String(user?.role_name || user?.role || '').toLowerCase();
      const isStaff = ['super_admin', 'admin', 'supervisor', 'supervisor_coach', 'coach', 'leader_coach', 'matchmaker', 'manager'].includes(_userRole);
      const isAdminUser = ['super_admin', 'admin'].includes(_userRole);
      const isExternalMatchmakerUser = user?.matchmaker_type === 'external';
      const isCompanyRep = user?.is_company_representative === true || user?.is_company_representative === 1;
      // Anyone with chat access — admin, external matchmaker, company rep,
      // matchmaker / manager / coach / supervisor — gets unread badge polling.
      // Mirrors `canUseMessages` further below so the icon and its badge stay
      // in lockstep.
      const isStaffWithChat = ['coach', 'leader_coach', 'supervisor_coach', 'supervisor', 'matchmaker', 'manager'].includes(_userRole);
      const canUseMessagesForFetch = isAdminUser || isExternalMatchmakerUser || isCompanyRep || isStaffWithChat;

      // Coach sees /coach/kpi, leader_coach sees /leader-coach/kpi-performance.
      // Both pages surface unacknowledged KPI items (warnings) — show a red dot
      // on the sidebar entry when the coach still has active warnings.
      const kpiStatsRes = isLeaderCoachUser
        ? api.get('/leader-coach/kpi/dashboard-stats').catch(() => ({ data: {} }))
        : isCoachUser
          ? api.get('/coach/kpi/dashboard-stats').catch(() => ({ data: {} }))
          : Promise.resolve({ data: {} });

      const [matchesRes, messagesRes, tasksRes, calendarRes, billingRes, notifRes, assignmentReqRes, approvalsRes, kpiRes, supervisionRes] = await Promise.allSettled([
        isStaff ? api.get('/matches?type=received&status=pending&limit=1').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        canUseMessagesForFetch ? api.get('/messages/unread').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isStaff ? api.get('/tasks?limit=1').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isStaff ? api.get('/calendar/today-count').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isStaff ? api.get('/billing/balance-due').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        api.get('/notifications?limit=1').catch(() => ({ data: {} })),
        isCoachUser ? api.get('/coach/assignment-requests-count').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isSupervisorUser ? api.get('/supervisor/kpi/pending-approvals').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        kpiStatsRes,
        isServiceSupervisor ? api.get('/service/review-count').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      ]);

      // Normalise KPI stats — both coach and leader-coach endpoints return
      // { warnings: { yellow_count, orange_count, red_count } }; treat the
      // sum as the "unread" count for the sidebar dot.
      const w = kpiRes.status === 'fulfilled' ? (kpiRes.value.data?.warnings || {}) : {};
      const kpiUnread =
        Number(w.yellow_count || 0) +
        Number(w.orange_count || 0) +
        Number(w.red_count || 0);

      setBadgeCounts({
        matches: matchesRes.status === 'fulfilled' ? (matchesRes.value.data?.pending_count || 0) : 0,
        messages: messagesRes.status === 'fulfilled' ? (messagesRes.value.data?.unread_count || 0) : 0,
        tasks: tasksRes.status === 'fulfilled' ? (tasksRes.value.data?.due_soon_count || tasksRes.value.data?.stats?.overdue || 0) : 0,
        calendar: calendarRes.status === 'fulfilled' ? (calendarRes.value.data?.today_count || 0) : 0,
        billing: billingRes.status === 'fulfilled' ? (billingRes.value.data?.balance_due_count || 0) : 0,
        notifications: notifRes.status === 'fulfilled' ? (notifRes.value.data?.unread_count || 0) : 0,
        assignmentRequests: assignmentReqRes.status === 'fulfilled' ? (assignmentReqRes.value.data?.pending_count || 0) : 0,
        pendingApprovals: approvalsRes.status === 'fulfilled' ? (approvalsRes.value.data?.count || approvalsRes.value.data?.pending_count || 0) : 0,
        kpi: kpiUnread,
        supervision: supervisionRes.status === 'fulfilled' ? (Number(supervisionRes.value.data?.total) || 0) : 0,
      });
    } catch (error) {
      console.log('Badge counts fetch error:', error);
    }
  };

  const navigation = [
    { name: 'nav.dashboard', path: '/', icon: LayoutDashboard },
    { name: 'nav.profiles', path: '/profiles', icon: Users, requiresProfilesView: true },
    { name: 'nav.directory', path: '/directory', icon: FolderSearch, hideForCoachSupervisor: true, hideForBelowTeamMatchmaker: true },
    // 匹配 / 约会安排 / 视频会议 / 语音通话 are hidden from the plain coach
    // sidebar — coaches work these flows from the client CRM modal instead.
    // The pages stay reachable by URL for every other role.
    { name: 'nav.matches', path: '/matches', icon: Sparkles, hideForSupervisor: true, hideForInterviewer: true, hideForCoach: true, hideForManager: true },
    { name: 'nav.datingSchedules', path: '/dating-schedules', icon: Heart, hideForSupervisor: true, hideForInterviewer: true, hideForCoach: true, hideForManager: true },
    // Hidden for every coaching / oversight role — coach, leader_coach (via its
    // own allow-list above), supervisor and manager all work meetings from the
    // client CRM modal. Admins keep the sidebar entries.
    { name: 'nav.videoMeetings', path: '/meetings', icon: Video, hideForCoach: true, hideForSupervisor: true, hideForManager: true, hideForMatchmaker: true },
    { name: 'nav.audioCalls', path: '/audio-calls', icon: Phone, meetingRoles: true, hideForCoach: true, hideForSupervisor: true, hideForManager: true },
    { name: 'nav.connections', path: '/connections', icon: Link2, externalOrAdminOnly: true },
    // 面谈 — public interview bookings + the interviewer's open hours.
    // Interviewer-role users (primary or secondary role) and admins.
    { name: 'nav.interviewBookings', fallback: '面谈预约', path: '/interview-bookings', icon: CalendarCheck, interviewRoles: true },
    { name: 'nav.interviewAvailability', fallback: '可面谈时间', path: '/interview-availability', icon: CalendarClock, interviewRoles: true },
    // Messages and Notifications entries intentionally hidden from the sidebar —
    // pages still exist at /messages and /notifications and are reachable
    // directly, but admins / coaches / matchmakers should not see sidebar
    // buttons for them.
    { name: 'nav.tasks', path: '/tasks', icon: CheckSquare, hideForCoachSupervisor: true, hideForInterviewer: true, hideForMatchmaker: true },
    // 日历 (/calendar) removed from the sidebar per request; the page is still reachable by URL.
    // KPI 绩效 sidebar entry hidden per request — page stays reachable by
    // URL, but the coach sidebar no longer surfaces it.
    // { name: 'nav.coachKpi', path: '/coach/kpi', icon: BarChart3, iconBadge: 'C', coachOnly: true },
    // 我的工资 uses BadgeDollarSign (distinct from finance / payroll which
    // both used the plain Wallet icon).
    { name: 'nav.myPayroll', path: '/my-payroll', icon: BadgeDollarSign, iconBadge: 'M', coachOnly: true },
    { name: 'nav.payroll', path: '/payroll', icon: Wallet, iconBadge: 'P', leaderOrSupervisorOnly: true },
    // KPI 绩效 / KPI 审核 sidebar entries are intentionally hidden — the
    // pages remain reachable by direct URL, but the navigation chrome
    // omits them per backend cleanup. To bring them back, uncomment.
    // { name: 'nav.leaderCoachKpiPerformance', path: '/leader-coach/kpi-performance', icon: BarChart3, iconBadge: 'L', leaderCoachOnly: true },
    // { name: 'nav.leaderCoachReview', path: '/leader-coach/kpi', icon: BarChart3, iconBadge: 'R', leaderCoachOnly: true },
    // KPI 审批 (/supervisor/kpi) hidden — replaced by 服务监督 below; the page
    // stays reachable by URL.
    // { name: 'nav.supervisorKpi', path: '/supervisor/kpi', icon: LineChart, iconBadge: 'S', supervisorOnly: true },
    // 服务监督 (VIP 服务流程 5.0) — every coach's work records + KPI for the
    // 主管 roles (leader / supervisor coach, supervisor, manager) and admins.
    // It replaces the old KPI entries and embeds 月度报告.
    { name: 'nav.serviceSupervision', fallback: '服务监督', path: '/service-supervision', icon: ShieldCheck, supervisionRoles: true },
    // 月度服务报告 — coaches / serving matchmakers write them. 主管 roles reach
    // them from 服务监督 → 月度报告, so the separate entry is coach-side only.
    { name: 'nav.serviceReports', fallback: '服务报告', path: '/service-reports', icon: FileText, serviceReportRoles: true, hideForMatchmaker: true, hideForServiceSupervisor: true },
    { name: 'nav.affiliate', path: '/affiliate', icon: Gift, hideForCoachSupervisor: true, hideForExternalMatchmaker: true },
    { name: 'nav.billing', path: '/billing', icon: CreditCard, hideForCoachSupervisor: true },
  ];

  // isExternalMatchmaker check for navigation filtering
  const isExternalMatchmaker = user?.matchmaker_type === 'external';

  const isAdmin = hasRole(['super_admin', 'admin']);
  const isSuperAdmin = hasRole(['super_admin']);
  // Editor role: can edit any profile + manage trends. Gets a standalone
  // Trends entry in the sidebar (no full admin section).
  const isEditor = hasRole(['editor']) || String(user?.role_name || user?.role || '').toLowerCase() === 'editor';
  // Manager role: gets the KPIv2 entry (analytics + reporting view).
  const isManagerUser = hasRole(['manager']) || String(user?.role_name || user?.role || '').toLowerCase() === 'manager';
  // 服务监督 roles: leader_coach / supervisor_coach / supervisor / manager / admin / super_admin (primary or secondary).
  const isServiceSupervisor = isAdmin || isManagerUser || isLeaderCoachUser || isSupervisorUser || isServiceSupervisorUser(user);

  // Messages visibility: admins, company reps, external/white-label matchmakers,
  // PLUS coach / leader_coach / supervisor / matchmaker — i.e. every staff
  // role gets their own messages tab. (Internal matchmaker page-level gate
  // still blocks them inside Messages.js itself, but the sidebar entry is now
  // visible so the page is reachable for any role that has chat access.)
  const isCompanyRepresentative = user?.is_company_representative === true || user?.is_company_representative === 1;
  const _role = String(user?.role_name || user?.role || '').toLowerCase();
  const isStaffWithChat = ['coach', 'leader_coach', 'supervisor_coach', 'supervisor', 'matchmaker', 'manager'].includes(_role);
  // Internal matchmakers below 团队合伙人 Team (affiliate tier level 3) are
  // restricted: no Directory entry, and /profiles is scoped to their own clients.
  const isInternalMatchmakerUser = _role === 'matchmaker' && user?.matchmaker_type !== 'external';
  const belowTeamTier = isInternalMatchmakerUser
    && (user?.affiliate_tier_level == null || Number(user.affiliate_tier_level) < 3);
  const canUseMessages = isAdmin || isExternalMatchmaker || isCompanyRepresentative || isStaffWithChat;

  const adminNavigation = [
    { name: 'nav.adminDashboard', path: '/admin', icon: Shield },
    { name: 'nav.users', path: '/admin/users', icon: Users },
    { name: 'nav.roles', path: '/admin/roles', icon: Layers, iconBadge: 'R', superAdminOnly: true },
    { name: 'nav.allProfiles', path: '/admin/profiles', icon: FileText },
    // 档案编辑审核 uses FileEdit (pencil over a doc) so it's visually
    // distinct from 全部档案 above which uses the plain FileText.
    { name: 'nav.profileEditReviews', path: '/admin/profile-edits', icon: FileEdit },
    { name: 'nav.signedDocuments', path: '/admin/contracts', icon: FileCheck },
    // 自定义字段 uses ListChecks (distinct from 角色 above which uses
    // Layers with 'R' badge) so the two stack-style icons are no longer
    // confused at a glance.
    { name: 'nav.customFields', path: '/admin/custom-fields', icon: ListChecks, iconBadge: 'F', superAdminOnly: true },
    { name: 'nav.coaches', path: '/admin/coaches', icon: UserCircle },
    // KPI 管理 (/admin/kpi) hidden — 服务监督 replaces it; URL still works.
    // { name: 'nav.kpiManagement', path: '/admin/kpi', icon: BarChart3 },
    // 语音通话 lives in the main sidebar (below 视频会议) for admin too — no
    // duplicate here.
    { name: 'nav.payroll', path: '/payroll', icon: Wallet, iconBadge: 'P' },
    // 工资单 (Payslip) — sits below 工资核算 with the Receipt icon for
    // visual distinction. Currently links to /payroll (same destination
    // as 工资核算 above); the page itself can later be split into
    // period-management vs slip-detail views without touching the menu.
    { name: 'nav.payslip', path: '/payroll', icon: Receipt, iconBadge: 'S' },
    // 后台约会安排 uses Calendar (an actual calendar) instead of the
    // Heart icon that main-sidebar /dating-schedules uses.
    { name: 'nav.adminDatingSchedules', path: '/admin/dating-schedules', icon: Calendar },
    // 后台匹配 uses Star instead of Sparkles for the same reason.
    { name: 'nav.adminMatches', path: '/admin/matches', icon: Star },
    { name: 'nav.trends', path: '/admin/trends', icon: TrendingUp },
    { name: 'nav.adminBilling', path: '/admin/billing', icon: DollarSign },
    { name: 'nav.finance', path: '/admin/finance', icon: Wallet, iconBadge: '$' },
    { name: 'nav.upgradeRequests', path: '/admin/upgrade-requests', icon: ArrowUpRight },
    // 推广人 uses Users2 (group-of-people) instead of the same Gift
    // icon as the main-sidebar 推广 entry — clear visual distinction
    // between "my affiliate dashboard" and the org-wide management page.
    { name: 'nav.affiliates', path: '/admin/affiliates', icon: Users2 },
    // 套餐管理 uses Package instead of the same CreditCard icon as
    // 账单 (/billing) so plans vs billing are immediately distinguishable.
    { name: 'nav.plans', path: '/admin/plans', icon: Package },
    { name: 'nav.adminExperiences', fallback: '活动与成长项目', path: '/admin/experiences', icon: CalendarDays },
    { name: 'nav.automationSettings', path: '/admin/automation-settings', icon: Zap },
    { name: 'nav.starsignSettings', path: '/admin/starsign-settings', icon: Star, superAdminOnly: true },
    { name: 'nav.starsignReports', path: '/admin/starsign-reports', icon: Sparkles },
    { name: 'nav.training', path: '/admin/training', icon: GraduationCap },
    { name: 'nav.platformSettings', path: '/admin/settings', icon: Settings },
    { name: 'nav.translations', path: '/admin/translations', icon: Globe },
    { name: 'nav.databaseSync', path: '/admin/database-sync', icon: Database, superAdminOnly: true },
    { name: 'nav.apiTokens', path: '/admin/api-tokens', icon: Code, superAdminOnly: true },
    { name: 'nav.activityLogs', path: '/admin/logs', icon: Activity },
  ];

  // All searchable pages
  const allPages = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, category: 'Main' },
    { name: 'Profiles', path: '/profiles', icon: Users, category: 'Main', requiresProfilesView: true },
    { name: 'Add New Profile', path: '/profiles/new', icon: Users, category: 'Main', requiresProfilesView: true },
    { name: 'Directory', path: '/directory', icon: FolderSearch, category: 'Main' },
    { name: 'Matches', path: '/matches', icon: Sparkles, category: 'Main', hideForSupervisor: true, hideForCoach: true },
    { name: 'Dating Schedules', path: '/dating-schedules', icon: Heart, category: 'Main', hideForSupervisor: true, hideForCoach: true },
    { name: 'Connections', path: '/connections', icon: Link2, category: 'Main' },
    { name: 'Messages', path: '/messages', icon: MessageCircle, category: 'Main', messagesOnly: true },
    { name: 'Notifications', path: '/notifications', icon: Bell, category: 'Main' },
    { name: 'Tasks', path: '/tasks', icon: CheckSquare, category: 'Main' },
    { name: 'Calendar', path: '/calendar', icon: Calendar, category: 'Main', hideForCoach: true },
    // KPI Performance sidebar entry hidden per request — see matching note above.
    // { name: 'KPI Performance', path: '/coach/kpi', icon: BarChart3, category: 'Main', coachOnly: true },
    // KPI Review (/leader-coach/kpi) removed from page search — 服务监督 replaces it.
    { name: 'Service Supervision', path: '/service-supervision', icon: ShieldCheck, category: 'Main', supervisionOnly: true },
    { name: 'Affiliate Program', path: '/affiliate', icon: Gift, category: 'Main', hideForCoach: true },
    { name: 'Billing', path: '/billing', icon: CreditCard, category: 'Main', hideForCoach: true },
    { name: 'Settings', path: '/settings', icon: Settings, category: 'Main' },
    { name: 'Admin Dashboard', path: '/admin', icon: Shield, category: 'Admin' },
    { name: 'User Management', path: '/admin/users', icon: Users, category: 'Admin' },
    { name: 'Role Management', path: '/admin/roles', icon: Layers, category: 'Admin' },
    { name: 'All Profiles', path: '/admin/profiles', icon: FileText, category: 'Admin' },
    { name: 'Custom Fields', path: '/admin/custom-fields', icon: Layers, category: 'Admin' },
    { name: 'Coaches', path: '/admin/coaches', icon: UserCircle, category: 'Admin' },
    { name: 'Admin Dating Schedules', path: '/admin/dating-schedules', icon: Heart, category: 'Admin' },
    { name: 'Admin Matches', path: '/admin/matches', icon: Sparkles, category: 'Admin' },
    { name: 'Trends Management', path: '/admin/trends', icon: TrendingUp, category: 'Admin' },
    { name: 'Admin Billing', path: '/admin/billing', icon: DollarSign, category: 'Admin' },
    { name: 'Affiliate Management', path: '/admin/affiliates', icon: Gift, category: 'Admin' },
    { name: 'Plans Management', path: '/admin/plans', icon: CreditCard, category: 'Admin' },
    { name: 'Auto-Response Manager', path: '/admin/auto-responses', icon: MessageSquare, category: 'Admin' },
    { name: 'Platform Settings', path: '/admin/settings', icon: Settings, category: 'Admin' },
    { name: 'Translations', path: '/admin/translations', icon: Globe, category: 'Admin' },
    { name: 'Database Sync', path: '/admin/database-sync', icon: Database, category: 'Admin' },
    { name: 'Activity Logs', path: '/admin/logs', icon: Activity, category: 'Admin' },
    { name: 'Signed Documents', path: '/admin/contracts', icon: FileCheck, category: 'Admin' },
  ];

  // Filter pages based on search term (exclude admin pages, respect visibility rules)
  const filteredPages = searchTerm.trim()
    ? allPages.filter(page => {
        if (page.category === 'Admin') return false;
        if (page.messagesOnly && !canUseMessages) return false;
        if (page.hideForCoachSupervisor && (isCoachUser || isSupervisorUser)) return false;
        if (page.hideForCoach && isCoachUser) return false;
        if (page.hideForSupervisor && isSupervisorUser) return false;
        if (page.path === '/directory' && belowTeamTier) return false;
        if (page.coachOnly && !isCoachUser) return false;
        if (page.supervisionOnly && !isServiceSupervisor) return false;
        if (page.requiresProfilesView && !hasPermission('profiles.view')) return false;
        const q = searchTerm.toLowerCase();
        const zhName = PAGE_NAME_ZH[page.path] || '';
        return page.name.toLowerCase().includes(q) ||
          zhName.toLowerCase().includes(q) ||
          page.category.toLowerCase().includes(q);
      })
    : [];

  // Handle search result click
  const handleSearchResultClick = (path) => {
    navigate(path);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Filter navigation based on user type
  const filteredNavigation = navigation.filter(item => {
    if (item.hideForBelowTeamMatchmaker && belowTeamTier) return false;
    // 面谈预约 / 可面谈时间: interviewer role (primary or secondary) + admins only.
    if (item.interviewRoles) return isInterviewerUser || isAdmin;
    // Leader Coach: only show specific menu items
    if (isLeaderCoachUser) {
      // 匹配 / 约会安排 / 视频会议 / 语音通话 are deliberately absent — those
      // flows are worked from the client CRM modal, not from the sidebar. The
      // pages stay reachable by URL.
      const allowedPaths = [
        '/',                              // Dashboard
        '/profiles',                      // Profiles
        '/notifications',                 // Notifications
        '/service-supervision',           // 服务监督 (replaces KPI Performance / KPI Review)
        '/payroll',                       // Payroll workflow (leader can submit)
        '/my-payroll',                    // Their own approved slips
        '/messages',                      // Staff chat
        ...(isInterviewerUser ? ['/interview-bookings', '/interview-availability'] : []),
      ];
      return allowedPaths.includes(item.path);
    }

    // External-matchmaker exclusions take precedence so an item flagged
    // hideForExternalMatchmaker disappears even if it would otherwise pass
    // the messagesOnly / canUseMessages gate (which allows external MMs).
    if (item.hideForExternalMatchmaker && isExternalMatchmaker) {
      return false;
    }
    // Messages: only for admins, company reps, external matchmakers, or users with messages permission
    if (item.messagesOnly) {
      return canUseMessages;
    }
    // Hide items for coach AND supervisor_coach roles
    if (item.hideForCoachSupervisor && (isCoachUser || isSupervisorUser)) {
      return false;
    }
    // Hide items for the plain coach role only (leader_coach / supervisor keep them)
    if (item.hideForCoach && isCoachUser) {
      return false;
    }
    // Hide items for the manager role
    if (item.hideForManager && isManagerUser) {
      return false;
    }
    // Hide items for supervisor only
    if (item.hideForSupervisor && isSupervisorUser) {
      return false;
    }
    // Hide items for interviewer-role users (matches, dating schedules, tasks)
    if (item.hideForInterviewer && isInterviewerUser) {
      return false;
    }
    // Hide items for matchmaker primary/secondary role (e.g. Tasks)
    if (item.hideForMatchmaker && isMatchmakerUser) {
      return false;
    }
    // Coach-only items (Assignment Requests)
    if (item.coachOnly && !isCoachUser) {
      return false;
    }
    // Supervisor-only items
    if (item.supervisorOnly && !isSupervisorUser) {
      return false;
    }
    // Leader-coach-only items
    if (item.leaderCoachOnly && !isLeaderCoachUser) {
      return false;
    }
    // Items visible to coach OR leader_coach OR admin (one-on-one audio calls
    // sit in the main sidebar for both staff who place the calls and admins
    // who review records).
    if (item.coachLeaderOrAdminOnly && !isCoachUser && !isLeaderCoachUser && !isAdmin) {
      return false;
    }
    // Same as coachLeaderOrAdminOnly but also includes interviewer users —
    // interviewer accounts also need to launch / review voice calls.
    if (item.coachLeaderInterviewerOrAdminOnly
        && !isCoachUser && !isLeaderCoachUser && !isAdmin && !isInterviewerUser) {
      return false;
    }
    // meetingRoles — any role that participates in client meetings
    // (coach / leader_coach / supervisor / supervisor_coach / interviewer /
    // admin). Used by the 语音会议 sidebar entry so supervisors and
    // leader_coaches see it alongside coaches.
    if (item.meetingRoles
        && !isCoachUser && !isLeaderCoachUser && !isSupervisorUser && !isInterviewerUser && !isAdmin) {
      return false;
    }
    // Items visible to leader_coach OR supervisor (e.g. shared payroll workflow)
    if (item.leaderOrSupervisorOnly && !isLeaderCoachUser && !isSupervisorUser) {
      return false;
    }
    // 服务监督: 主管 roles (primary or secondary) + admin
    if (item.supervisionRoles && !isServiceSupervisor) {
      return false;
    }
    // 服务报告 as its own entry is for the serving side; 主管 roles open it from 服务监督.
    // …except leader / supervisor coaches: they also serve members and write their own reports.
    if (item.hideForServiceSupervisor && isServiceSupervisor
        && !isLeaderCoachUser && String(user?.role || '').toLowerCase() !== 'supervisor_coach') {
      return false;
    }
    // 服务报告: coach / leader_coach / supervisor(_coach) / manager / internal matchmaker / admin
    if (item.serviceReportRoles
        && !isCoachUser && !isLeaderCoachUser && !isSupervisorUser && !isManagerUser && !isInternalMatchmakerUser && !isAdmin) {
      return false;
    }
    // Profiles: require profiles.view permission
    if (item.requiresProfilesView && !hasPermission('profiles.view')) {
      return false;
    }
    if (item.externalOrAdminOnly) {
      return isExternalMatchmaker || isAdmin;
    }
    return true;
  });

  // Get badge count for a navigation item
  const getBadgeCount = (itemName) => {
    switch (itemName) {
      case 'nav.matches': return badgeCounts.matches;
      case 'nav.messages': return badgeCounts.messages;
      case 'nav.tasks': return badgeCounts.tasks;
      case 'nav.calendar': return badgeCounts.calendar;
      case 'nav.billing': return badgeCounts.billing;
      case 'nav.notifications': return badgeCounts.notifications;
      case 'nav.profiles': return badgeCounts.assignmentRequests;
      case 'nav.supervisorKpi': return badgeCounts.pendingApprovals;
      case 'nav.serviceSupervision': return badgeCounts.supervision;
      case 'nav.coachKpi': return badgeCounts.kpi;
      case 'nav.leaderCoachKpiPerformance': return badgeCounts.kpi;
      default: return 0;
    }
  };

  // ---------------------------------------------------------------------
  // Simplified shell presentation. Nothing below changes WHO sees WHAT —
  // `filteredNavigation`, the admin list and the role gates above still
  // decide visibility. This part only groups the visible items into a few
  // plain-language sections, and adds a phone bottom tab bar + avatar menu.
  // ---------------------------------------------------------------------

  // Plain-language display names for a few nav keys whose translations read
  // technical ("仪表板", "目录", "联盟"). Translation keys stay untouched;
  // anything not listed here falls back to t(key).
  const NAV_LABEL_OVERRIDES = {
    zh: {
      'nav.dashboard': '首页',
      'nav.directory': '会员库',
      'nav.affiliate': '推广赚佣金',
      'nav.connections': '合作连接',
      'nav.myPayroll': '我的工资',
      'nav.kpiV2': '服务绩效',
      'nav.admin': '管理员',
      'nav.organizerEvents': '活动管理',
      'nav.organizerApply': '申请办活动',
      'nav.organizerPrograms': '成长项目管理',
      'nav.organizerApplyMore': '申请开通',
      'nav.organizerCheckin': '扫码签到',
      'nav.organizerEarnings': '收入与结算',
      'nav.adminExperiences': '活动与成长项目',
      'nav.serviceReports': '服务报告',
      'nav.serviceSupervision': '服务监督',
      'nav.interviewBookings': '面谈预约',
      'nav.interviewAvailability': '可面谈时间',
    },
    en: {
      'nav.dashboard': 'Home',
      'nav.directory': 'Member Library',
      'nav.affiliate': 'Refer & Earn',
      'nav.admin': 'Admin',
      'nav.organizerEvents': 'My events',
      'nav.organizerApply': 'Host events',
      'nav.organizerPrograms': 'My programmes',
      'nav.organizerApplyMore': 'Apply to host',
      'nav.organizerCheckin': 'Check-in',
      'nav.organizerEarnings': 'Earnings',
      'nav.adminExperiences': 'Events & programmes',
      'nav.serviceReports': 'Service reports',
      'nav.serviceSupervision': 'Supervision',
      'nav.interviewBookings': 'Interview bookings',
      'nav.interviewAvailability': 'Interview hours',
    },
  };
  const navLabel = (key, fallback) => (NAV_LABEL_OVERRIDES[isZh ? 'zh' : 'en'][key]) || t(key, fallback);

  // Which group each main-menu path belongs to. Unlisted paths go to "mine".
  const NAV_GROUP_OF = {
    '/': 'daily', '/profiles': 'daily', '/directory': 'daily', '/matches': 'daily',
    '/dating-schedules': 'daily', '/connections': 'daily', '/tasks': 'daily',
    '/meetings': 'talk', '/audio-calls': 'talk',
    '/service-reports': 'daily',
    '/service-supervision': 'daily',
    '/interview-bookings': 'interview', '/interview-availability': 'interview',
  };
  const NAV_GROUPS = [
    { id: 'daily', zh: '日常工作', en: 'Daily work' },
    { id: 'interview', zh: '面谈', en: 'Interviews' },
    { id: 'talk', zh: '沟通', en: 'Talk to clients' },
    { id: 'mine', zh: '我的', en: 'My account' },
  ];

  // Entries that used to live in the bottom block; same visibility rules.
  // 服务绩效 (KPIv2) — sidebar entry for admins only (hidden for every other
  // role per request; /kpi-v2 itself still opens by URL).
  // 服务绩效 (KPIv2) — hidden for admins too now: 服务监督 replaces the old KPI
  // screens. /kpi-v2 still opens by URL.
  const showKpiV2 = false;
  // 问题反馈 — coach / leader_coach / supervisor / supervisor_coach + admin.
  const showFeedback = isCoachUser || isLeaderCoachUser || isSupervisorUser || isAdmin;
  // 操作引导 (/guide) — every staff role that can open 客户档案 (the 客户工作台 lives there).
  const tour = useTour();
  const showGuide = filteredNavigation.some((it) => it.path === '/profiles');
  // 培训 — hidden for coach / leader_coach / supervisor / external matchmaker.
  const showTraining = !(isCoachUser || isLeaderCoachUser || isSupervisorUser || isExternalMatchmaker);

  // 活动与成长项目: organisers with the event module (and admins) get 活动管理,
  // with the program module 成长项目管理; anyone with a module (or who is a
  // check-in helper on a current event) gets 扫码签到; module holders and
  // anyone with 推广 get 收入与结算. Matchmakers see 申请办活动 while some module isn't enabled yet.
  const [organizerMe, setOrganizerMe] = useState(null);
  useEffect(() => {
    if (!user || isAdmin) return undefined;
    let alive = true;
    organizerAPI.getMe()
      .then((r) => { if (alive) setOrganizerMe(r.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user, isAdmin]);
  const orgModules = Array.isArray(organizerMe?.modules) ? organizerMe.modules : [];
  const moduleOn = (k) => orgModules.some((x) => x.module === k && x.status === 'enabled');
  const eventModuleEnabled = moduleOn('event');
  const programModuleEnabled = moduleOn('program');
  const anyModuleEnabled = eventModuleEnabled || programModuleEnabled;
  const showOrganizerEvents = isAdmin || eventModuleEnabled;
  const showOrganizerPrograms = isAdmin || programModuleEnabled;
  const showOrganizerCheckin = isAdmin || anyModuleEnabled || Number(organizerMe?.helper_of) > 0;
  // 收入与结算 is also the single place for 推广佣金 and its payout requests,
  // so everyone who can see 推广 (/affiliate) gets it too — not only module
  // holders. (extraMineItems bypass the leader-coach allowedPaths list.)
  const canSeeAffiliate = filteredNavigation.some((it) => it.path === '/affiliate');
  const showOrganizerEarnings = isAdmin || anyModuleEnabled || canSeeAffiliate;
  const showOrganizerApply = !isAdmin && isMatchmakerUser && !(eventModuleEnabled && programModuleEnabled);

  const extraMineItems = [
    showOrganizerEvents && { name: 'nav.organizerEvents', fallback: isZh ? '活动管理' : 'My events', path: '/organizer/events', icon: CalendarDays },
    showOrganizerPrograms && { name: 'nav.organizerPrograms', fallback: isZh ? '成长项目管理' : 'My programmes', path: '/organizer/programs', icon: Sprout },
    showOrganizerCheckin && { name: 'nav.organizerCheckin', fallback: isZh ? '扫码签到' : 'Check-in', path: '/organizer/checkin', icon: ScanLine },
    showOrganizerEarnings && { name: 'nav.organizerEarnings', fallback: isZh ? '收入与结算' : 'Earnings', path: '/organizer/earnings', icon: Banknote },
    showOrganizerApply && (anyModuleEnabled
      ? { name: 'nav.organizerApplyMore', fallback: isZh ? '申请开通' : 'Apply to host', path: '/organizer/apply', icon: CalendarPlus }
      : { name: 'nav.organizerApply', fallback: isZh ? '申请办活动' : 'Host events', path: '/organizer/apply', icon: CalendarPlus }),
    showKpiV2 && { name: 'nav.kpiV2', fallback: isZh ? 'KPIv2 绩效' : 'KPIv2', path: '/kpi-v2', icon: Target },
    showTraining && { name: 'nav.training', fallback: isZh ? '培训' : 'Training', path: '/training', icon: GraduationCap },
  ].filter(Boolean);

  const groupedNav = NAV_GROUPS.map((g) => ({
    ...g,
    items: [
      ...filteredNavigation.filter((item) => (NAV_GROUP_OF[item.path] || 'mine') === g.id),
      ...(g.id === 'mine' ? extraMineItems : []),
    ],
  })).filter((g) => g.items.length > 0);

  const visibleAdminNav = isAdmin ? adminNavigation.filter((item) => !item.superAdminOnly || isSuperAdmin) : [];

  // Collapsible 管理员 group. Remembered per browser; forced open while the
  // user is on an /admin page so the active item is always visible.
  const onAdminPage = (location.pathname || '').startsWith('/admin');
  const [adminOpen, setAdminOpen] = useState(() => {
    try {
      const v = localStorage.getItem('mm_nav_admin_open');
      if (v === '1' || v === '0') return v === '1';
    } catch (_) { /* ignore */ }
    return true;
  });
  const toggleAdminOpen = () => {
    setAdminOpen((v) => {
      const nv = !v;
      try { localStorage.setItem('mm_nav_admin_open', nv ? '1' : '0'); } catch (_) { /* ignore */ }
      return nv;
    });
  };
  const adminExpanded = adminOpen || onAdminPage;

  // Avatar (account) menu — phone/tablet header.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onDown = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) setAccountMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setAccountMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountMenuOpen]);
  useEffect(() => { setAccountMenuOpen(false); }, [location.pathname]);

  // Page title for the phone/tablet header (longest matching nav path).
  const getPageTitle = () => {
    const path = location.pathname || '/';
    if (path === '/') return navLabel('nav.dashboard');
    const pool = [
      ...filteredNavigation, ...extraMineItems, ...visibleAdminNav,
      { name: 'nav.messages', path: '/messages' },
      { name: 'nav.settings', path: '/settings' },
      { name: 'nav.feedback', fallback: isZh ? '问题反馈' : 'Feedback', path: '/help' },
      { name: 'nav.guide', fallback: isZh ? '操作引导' : 'Guided tour', path: '/guide' },
    ];
    let best = null;
    pool.forEach((item) => {
      if (item.path === '/') return;
      if ((path === item.path || path.startsWith(item.path + '/')) && (!best || item.path.length > best.path.length)) best = item;
    });
    if (best) return navLabel(best.name, best.fallback);
    if (isZh && PAGE_NAME_ZH[path]) return PAGE_NAME_ZH[path];
    return roleLabel;
  };

  const roleLabel = (() => {
    const r = user?.role?.toLowerCase();
    if (r === 'coach') return t('roles.coach', 'Coach');
    if (r === 'leader_coach') return t('roles.leaderCoach', 'Leader Coach');
    if (r === 'admin' || r === 'super_admin') return t('roles.admin', 'Admin');
    if (r === 'supervisor' || r === 'supervisor_coach') return t('roles.supervisor', 'Supervisor');
    return t('roles.matchmaker', 'Matchmaker');
  })();

  const displayFirstName = user?.first_name || user?.firstName || (user?.name || 'User').split(' ')[0];
  const avatarPhoto = (user?.profile_photo || user?.avatar) ? getPhotoUrl(user.profile_photo || user.avatar) : null;
  const avatarInitials = getInitials(user?.name || user?.first_name || user?.firstName || 'User');

  // ---- Phone bottom tab bar: 4 most-used pages the role can see + 更多 ----
  // Messages has no sidebar entry (intentionally) but every chat-capable role
  // can open it, and its unread count is already polled — so it earns a tab.
  const TAB_PRIORITY = ['/', '/profiles', '/messages', '/matches', '/dating-schedules', '/meetings',
    '/my-payroll', '/payroll', '/service-supervision', '/supervisor/kpi', '/tasks', '/directory', '/affiliate', '/billing', '/kpi-v2', '/training'];
  const TAB_SHORT = {
    '/': ['首页', 'Home'], '/profiles': ['档案', 'Profiles'], '/messages': ['消息', 'Messages'],
    '/matches': ['匹配', 'Matches'], '/dating-schedules': ['约会', 'Dates'], '/meetings': ['会议', 'Meetings'],
    '/my-payroll': ['工资', 'Pay'], '/payroll': ['工资', 'Payroll'], '/supervisor/kpi': ['KPI', 'KPI'], '/service-supervision': ['监督', 'Supervise'],
    '/tasks': ['任务', 'Tasks'], '/directory': ['会员库', 'Library'], '/affiliate': ['推广', 'Refer'],
    '/billing': ['账单', 'Billing'], '/kpi-v2': ['绩效', 'KPI'], '/training': ['培训', 'Training'],
  };
  const tabCandidates = [
    ...filteredNavigation, ...extraMineItems,
    ...(canUseMessages ? [{ name: 'nav.messages', path: '/messages', icon: MessageCircle }] : []),
  ];
  // Roles that have 推广赚佣金 get it in the third tab instead of 消息 (per
  // request); roles without it (coaches, external matchmakers) keep 消息.
  const hasAffiliateTab = tabCandidates.some((it) => it.path === '/affiliate');
  const tabPriority = hasAffiliateTab
    ? ['/', '/profiles', '/affiliate', ...TAB_PRIORITY.filter((p) => !['/', '/profiles', '/affiliate', '/messages'].includes(p))]
    : TAB_PRIORITY;
  const tabItems = tabPriority
    .map((p) => tabCandidates.find((it) => it.path === p))
    .filter(Boolean)
    .slice(0, 4);
  const isTabActive = (p) => (p === '/' ? location.pathname === '/' : (location.pathname === p || location.pathname.startsWith(p + '/')));
  const anyTabActive = tabItems.some((it) => isTabActive(it.path));
  // Full-height chat screens have their own composer at the bottom — no tab bar there.
  const showTabBar = isPhone && !(location.pathname || '').startsWith('/chat');

  // Bottom-right chip drawn over a sidebar icon to disambiguate items that
  // share the same lucide icon (e.g. multiple Wallet / BarChart3 entries).
  const iconBadgeStyle = {
    position: 'absolute',
    bottom: -4, right: -6,
    minWidth: 14, height: 14, padding: '0 3px',
    borderRadius: 7,
    background: S.ink, color: S.paper,
    fontSize: 9, fontWeight: 500, lineHeight: '14px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    pointerEvents: 'none',
    boxShadow: `0 0 0 1.5px ${S.paper}`, // a ring in the ground colour, not a shadow
  };

  const countBadge = (n, compact) => (
    <span className="dl-count" style={{
      display: 'flex',
      position: 'absolute',
      right: compact ? 6 : 10,
      top: compact ? 4 : '50%',
      transform: compact ? 'none' : 'translateY(-50%)',
      background: S.gold, color: S.card, fontFamily: S.sans, fontSize: compact ? 10.5 : 12, fontWeight: 400, letterSpacing: 0,
      minWidth: compact ? 18 : 22, height: compact ? 18 : 22, borderRadius: compact ? 9 : 11, boxSizing: 'border-box',
      alignItems: 'center', justifyContent: 'center', padding: compact ? '0 4px' : '0 6px',
    }}>
      {n > 99 ? '99+' : n}
    </span>
  );

  // One nav row (sidebar). Hover tooltip on the collapsed rail, badge, active state.
  const renderNavItem = (item, { end, badge = 0, dot = false } = {}) => {
    const label = navLabel(item.name, item.fallback);
    const Icon = item.icon;
    return (
      <div
        key={item.name + item.path}
        style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
        className="nav-item-wrapper"
        onMouseEnter={(e) => showCollapsedTooltip(label, e.currentTarget)}
        onMouseLeave={hideCollapsedTooltip}
      >
        <NavLink
          to={item.path}
          end={end}
          className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          onClick={() => { setSidebarOpen(false); hideCollapsedTooltip(); }}
          title={sidebarCollapsed ? label : undefined}
          style={{ position: 'relative', flex: 1 }}
        >
          <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <Icon size={18} strokeWidth={1.5} />
            {item.iconBadge && <span style={iconBadgeStyle}>{item.iconBadge}</span>}
          </span>
          {!sidebarCollapsed && <span className="dl-label">{label}</span>}
          {badge > 0 && countBadge(badge, sidebarCollapsed)}
          {dot && !badge && (
            <span aria-label={isZh ? '还没看完' : 'Not finished'} style={{
              position: 'absolute', right: sidebarCollapsed ? 8 : 14, top: sidebarCollapsed ? 6 : '50%', transform: sidebarCollapsed ? 'none' : 'translateY(-50%)',
              width: 8, height: 8, borderRadius: '50%', background: S.bad,
            }} />
          )}
        </NavLink>
      </div>
    );
  };

  const groupHeader = (text) => (sidebarCollapsed
    ? <div className="dl-divider" aria-hidden="true" />
    : <div className="dl-group-title">{text}</div>);

  const languageToggle = (dark) => (
    // `dark` = sits inside the sidebar (now ivory too — kept for callers).
    <span style={{ display: 'inline-flex', background: dark ? 'transparent' : S.card, border: `1px solid ${S.line}`, borderRadius: S.radius, padding: 2 }}>
      {[['zh', '中'], ['en', 'EN']].map(([code, label]) => (
        <button key={code} type="button" onClick={() => setLanguage(code)}
          style={{
            border: 'none', cursor: 'pointer', borderRadius: 0, padding: '3px 11px',
            fontSize: 11.5, fontWeight: 400, letterSpacing: '.12em', fontFamily: 'inherit',
            background: language === code ? S.ink : 'transparent',
            color: language === code ? S.paper : S.sub,
          }}>
          {label}
        </button>
      ))}
    </span>
  );

  const avatarCircle = (size) => (
    avatarPhoto ? (
      <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'inline-block', backgroundImage: `url(${avatarPhoto})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: S.soft }} />
    ) : (
      <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: S.soft, color: S.gold, fontFamily: S.serif, fontWeight: 500, fontSize: Math.round(size * 0.48) }}>
        {avatarInitials}
      </span>
    )
  );

  const menuRowStyle = {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 16px',
    background: 'none', border: 'none', textAlign: 'left', fontSize: 14, letterSpacing: '.04em', color: S.ink,
    fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'none', boxSizing: 'border-box',
  };

  const footerIconBtn = { background: 'transparent', color: S.sub, width: 34, height: 34, minHeight: 0, border: `1px solid ${S.line}`, borderRadius: S.radius };

  return (
    <div className={`dashboard-layout dl-simple staff-luxe ${showTabBar ? 'dl-has-tabbar' : ''}`}>
      <style data-staff-luxe-fonts>{LUXE_FONTS_CSS}</style>
      <style>{`
        .dl-simple .sidebar { padding: 28px 18px 18px; }
        .dl-simple .sidebar.collapsed { padding: 22px 0.5rem 16px; }
        .dl-simple .sidebar-logo { margin-bottom: 22px; font-size: 1rem; }
        .dl-simple .dl-logo-btn { display: flex; align-items: center; gap: 10px; width: 100%; background: none; border: none; padding: 0 4px; cursor: pointer; color: ${S.ink}; font: inherit; text-align: left; }
        .dl-simple .sidebar.collapsed .dl-logo-btn { flex-direction: column; justify-content: center; gap: 10px; padding: 0; }
        .dl-simple .dl-wordmark { font-family: ${S.serif}; font-size: 26px; font-weight: 400; letter-spacing: .14em; line-height: 1; color: ${S.ink}; }
        .dl-simple .dl-logo-toggle { margin-left: auto; display: inline-flex; color: ${S.sub}; }
        .dl-simple .dl-logo-btn:hover .dl-logo-toggle { color: ${S.ink}; }
        .dl-simple .sidebar.collapsed .dl-logo-toggle { margin-left: 0; }
        .dl-simple .sidebar-nav { gap: 0; }
        .dl-simple .nav-section { margin-bottom: 18px; display: flex; flex-direction: column; gap: 2px; }
        .dl-simple .nav-link { padding: 10px 12px; gap: 12px; font-size: 14px; letter-spacing: .04em; line-height: 1.3; border-radius: 0; }
        .dl-simple .sidebar.collapsed .nav-link { padding: 11px; }
        .dl-simple .nav-link.active { background: ${S.ink}; color: ${S.paper}; font-weight: 400; }
        .dl-simple .nav-link .dl-label { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding-right: 30px; }
        .dl-simple .dl-group-title { font-size: 11px; font-weight: 400; color: ${S.sub}; padding: 4px 12px 8px; letter-spacing: .24em; }
        .dl-simple .dl-divider { height: 1px; background: ${S.line}; margin: 6px 10px 8px; }
        .dl-simple .dl-admin-toggle { display: flex; align-items: center; width: 100%; gap: 8px; background: none; border: none; cursor: pointer; font-family: inherit; text-align: left; color: ${S.sub}; }
        .dl-simple .dl-admin-toggle:hover .dl-group-title { color: ${S.ink}; }
        .dl-simple .sidebar.collapsed .dl-admin-toggle { justify-content: center; padding: 6px 0; color: ${S.sub}; }
        .dl-simple .dl-search { display: flex; align-items: center; gap: 8px; border-bottom: 1px solid rgba(17,17,17,.35); padding: 6px 2px; margin: 0 4px; }
        .dl-simple .dl-search input { flex: 1; min-width: 0; border: none; background: transparent; color: ${S.ink}; font-family: inherit; outline: none; padding: 0; height: 26px; letter-spacing: .02em; }
        .dl-simple .dl-search input::placeholder { color: ${S.sub}; font-weight: 300; }
        .dl-simple .dl-search:focus-within { border-bottom-color: ${S.ink}; }
        .dl-simple .dl-search-results button:hover { background: ${S.soft} !important; }
        .dl-simple .sidebar-footer { padding-top: 16px; }
        .dl-simple .sidebar-footer .btn-icon:hover:not(:disabled) { color: ${S.ink}; border-color: ${S.ink}; background: transparent; }
        .dl-mobile-title { font-family: ${S.serif}; font-size: 22px; font-weight: 500; color: ${S.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
        .dl-simple .mobile-menu-btn { background: transparent !important; border: 1px solid ${S.line} !important; border-radius: ${S.radius}px !important; color: ${S.ink} !important; }
        .dl-account-btn { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; padding: 0; border-radius: 50%; border: 1px solid ${S.line}; background: transparent; cursor: pointer; }
        .dl-account-menu { position: absolute; right: 0; top: calc(100% + 8px); width: 248px; background: ${S.card}; border: 1px solid rgba(17,17,17,.2); border-radius: ${S.radius}px; box-shadow: none; overflow: hidden; z-index: 1001; }
        .dl-account-menu .dl-row { color: ${S.ink}; }
        .dl-account-menu .dl-row:hover { background: ${S.soft}; }
        .dl-tabbar {
          position: fixed; left: 0; right: 0; bottom: 0; z-index: 85;
          display: flex; background: ${S.paper}; border-top: 1px solid ${S.line};
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        .dl-tab {
          flex: 1; min-width: 0; height: 58px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
          color: ${S.sub}; text-decoration: none; background: none; border: none; font-family: inherit; cursor: pointer; padding: 0; position: relative;
          -webkit-tap-highlight-color: transparent;
        }
        .dl-tab svg { stroke-width: 1.5; }
        .dl-tab span.dl-tab-label { font-size: 11px; font-weight: 400; letter-spacing: .14em; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
        .dl-tab.active, .dl-tab.active:hover { color: ${S.ink}; }
        .dl-tab.active::before { content: ''; position: absolute; top: -1px; left: 30%; right: 30%; height: 1px; background: ${S.ink}; }
        .dl-tab-badge { position: absolute; top: 5px; left: calc(50% + 6px); min-width: 17px; height: 17px; padding: 0 4px; box-sizing: border-box; border-radius: 9px; background: ${S.gold}; color: ${S.card}; font-size: 10.5px; font-weight: 400; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 2px ${S.paper}; }
        .dl-has-tabbar .main-body { padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; }
        body:has(.mx-in-chat) .dl-tabbar { display: none; }
        body:has(.mx-in-chat) .dl-has-tabbar .main-body { padding-bottom: var(--space-md, 16px) !important; }
        @media (min-width: 768px) { .dl-tabbar { display: none; } }
        @media (max-width: 992px) {
          .main-header .search-input-wrapper { display: none; }
          .main-header button[style*="display: none"] { display: flex !important; }
        }
      `}</style>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="modal-overlay"
          style={{ zIndex: 90 }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* The logo slot IS the sidebar toggle — on desktop it collapses /
            expands the rail, in the phone/tablet drawer it closes the drawer. */}
        <div className="sidebar-logo">
          <button
            type="button"
            className="dl-logo-btn"
            onClick={() => (isMobile ? setSidebarOpen(false) : setSidebarCollapsed(v => !v))}
            title={isMobile ? t('nav.close', isZh ? '关闭' : 'Close')
              : (sidebarCollapsed ? t('nav.expand') : t('nav.collapse'))}
            aria-label={isMobile ? t('nav.close', isZh ? '关闭' : 'Close')
              : (sidebarCollapsed ? t('nav.expand') : t('nav.collapse'))}
          >
            <DiamondMark size={22} strokeWidth={1.4} />
            {!sidebarCollapsed && <span className="dl-wordmark">FINDU</span>}
            {/* inline display: index.css hides `.sidebar.collapsed .sidebar-logo span` */}
            <span className="dl-logo-toggle" style={{ display: 'inline-flex' }}>
              {isMobile ? <X size={20} strokeWidth={1.5} /> : (sidebarCollapsed ? <PanelLeft size={18} strokeWidth={1.5} /> : <PanelLeftClose size={18} strokeWidth={1.5} />)}
            </span>
          </button>
        </div>

        {/* 页面搜索 — hidden while the sidebar is collapsed (no room at 80px). */}
        {!sidebarCollapsed && (
          <div ref={searchRef} style={{ position: 'relative', marginBottom: 22 }}>
            <label className="dl-search">
            <Search size={15} strokeWidth={1.4} color={S.sub} style={{ flexShrink: 0, pointerEvents: 'none' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setShowSearchResults(true); }}
              onFocus={() => setShowSearchResults(true)}
              placeholder={isZh ? '找页面…' : t('nav.searchPages')}
              style={{ fontSize: isMobile ? 16 : 13.5 }}
            />
            </label>
            {showSearchResults && searchTerm.trim() && (
              <div className="dl-search-results" style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: S.card,
                borderRadius: S.radius, border: '1px solid rgba(17,17,17,.2)',
                maxHeight: 320, overflowY: 'auto', zIndex: 1001,
              }}>
                {filteredPages.length === 0 ? (
                  <div style={{ padding: '0.75rem', textAlign: 'center', color: S.sub, fontSize: 12.5 }}>
                    {isZh ? '未找到匹配的页面' : 'No pages found'}
                  </div>
                ) : filteredPages.map((page, idx) => {
                  const IconComponent = page.icon;
                  return (
                    <button key={page.path + idx} onClick={() => { handleSearchResultClick(page.path); setSidebarOpen(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        border: 'none', borderBottom: `1px solid ${S.lineSoft}`, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                      <IconComponent size={15} strokeWidth={1.5} color={S.gold} />
                      <span style={{ fontSize: 13.5, color: S.ink, letterSpacing: '.02em' }}>{isZh ? (PAGE_NAME_ZH[page.path] || page.name) : page.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <nav className="sidebar-nav">
          {/* 日常工作 / 沟通 / 我的 — same items as before, just grouped. */}
          {groupedNav.map((g, gi) => (
            <div className="nav-section" key={g.id}>
              {(gi > 0 || !sidebarCollapsed) && groupHeader(isZh ? g.zh : g.en)}
              {g.items.map((item) => renderNavItem(item, { end: item.path === '/', badge: getBadgeCount(item.name) }))}
            </div>
          ))}

          {/* 管理员 — collapsible, kept separate from the daily menu. */}
          {isAdmin && (
            <div className="nav-section">
              {sidebarCollapsed ? (
                <div
                  style={{ position: 'relative' }}
                  onMouseEnter={(e) => showCollapsedTooltip(`${navLabel('nav.admin')} · ${adminExpanded ? t('nav.collapse') : t('nav.expand')}`, e.currentTarget)}
                  onMouseLeave={hideCollapsedTooltip}
                >
                  <div className="dl-divider" aria-hidden="true" />
                  <button type="button" className="dl-admin-toggle" onClick={toggleAdminOpen}
                    aria-expanded={adminExpanded} title={navLabel('nav.admin')}>
                    <Shield size={16} />
                    {adminExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                </div>
              ) : (
                <button type="button" className="dl-admin-toggle" onClick={toggleAdminOpen} aria-expanded={adminExpanded}
                  style={{ padding: 0, marginTop: 4 }}>
                  <span className="dl-group-title" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Shield size={13} strokeWidth={1.5} />
                    {navLabel('nav.admin')}
                    <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                      {adminExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                  </span>
                </button>
              )}
              {adminExpanded && visibleAdminNav.map((item) => renderNavItem(item, { end: item.path === '/admin' }))}
            </div>
          )}

          {/* 趋势管理 is admin-only in the sidebar (the editors' standalone entry was removed per request;
              /admin/trends still opens by URL). */}

          {/* Help & settings — pinned to the bottom of the menu. */}
          <div className="nav-section" style={{ marginTop: 'auto', paddingTop: 4 }}>
            <div className="dl-divider" aria-hidden="true" />
            {showGuide && renderNavItem({ name: 'nav.guide', fallback: isZh ? '操作引导' : 'Guided tour', path: '/guide', icon: HelpCircle }, { dot: tour.unfinished })}
            {showFeedback && renderNavItem({ name: 'nav.feedback', fallback: isZh ? '问题反馈' : 'Feedback', path: '/help', icon: Bug })}
            {renderNavItem({ name: 'nav.settings', path: '/settings', icon: Settings })}

            {/* 语言切换 — collapsed rail shows a single button that flips 中 / EN. */}
            <div
              style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
              className="nav-item-wrapper"
              onMouseEnter={(e) => showCollapsedTooltip(isZh ? '语言 · 切换到 English' : 'Language · 切换到中文', e.currentTarget)}
              onMouseLeave={hideCollapsedTooltip}
            >
              {sidebarCollapsed ? (
                <button
                  type="button"
                  onClick={() => { setLanguage(language === 'zh' ? 'en' : 'zh'); hideCollapsedTooltip(); }}
                  className="nav-link"
                  title={isZh ? '语言' : 'Language'}
                  style={{ flex: 1, width: '100%', background: 'none', border: 'none', font: 'inherit', cursor: 'pointer', justifyContent: 'center' }}
                >
                  <Globe size={18} strokeWidth={1.5} />
                </button>
              ) : (
                <div className="nav-link" style={{ flex: 1, cursor: 'default', display: 'flex', alignItems: 'center' }}>
                  <Globe size={18} strokeWidth={1.5} />
                  <span style={{ flex: 1 }}>{isZh ? '语言' : 'Language'}</span>
                  {languageToggle(true)}
                </div>
              )}
            </div>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info" style={sidebarCollapsed ? { justifyContent: 'center', padding: '0.5rem 0', flexDirection: 'column', gap: 8 } : { padding: '2px 4px', gap: 10 }}>
            {!sidebarCollapsed && (
              <>
                {avatarPhoto ? (
                  <div className="user-avatar" style={{ width: 36, height: 36, backgroundImage: `url(${avatarPhoto})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                ) : (
                  <div className="user-avatar" style={{ width: 36, height: 36 }}>{avatarInitials}</div>
                )}
                <div className="user-details">
                  <div className="user-name" title={user?.name || `${user?.first_name || user?.firstName || ''} ${user?.last_name || user?.lastName || ''}`.trim() || 'User'}>
                    {displayFirstName}
                  </div>
                  <div className="user-role">{roleLabel}</div>
                </div>
              </>
            )}
            {/* 清除缓存 — every role; small icon here instead of a full menu row. */}
            <button
              type="button"
              onClick={handleClearCache}
              disabled={clearingCache}
              className="btn btn-icon"
              style={{ ...footerIconBtn, cursor: clearingCache ? 'wait' : 'pointer', opacity: clearingCache ? 0.6 : 1 }}
              title={clearingCache ? (isZh ? '清除中…' : 'Clearing…') : t('nav.clearCache', isZh ? '清除缓存' : 'Clear cache')}
              aria-label={t('nav.clearCache', isZh ? '清除缓存' : 'Clear cache')}
            >
              <Eraser size={16} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="btn btn-icon"
              style={footerIconBtn}
              title={t('nav.logout')}
              aria-label={t('nav.logout')}
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Hover tooltip for collapsed sidebar — position: fixed escapes the
          sidebar's overflow clip so the label renders to the right of the icon. */}
      {sidebarCollapsed && collapsedTooltip && !isMobile && (
        <div
          style={{
            position: 'fixed',
            left: 80 + 6,                                  // sidebar (80px) + arrow (6px)
            top: collapsedTooltip.top,
            transform: 'translateY(-50%)',
            zIndex: 10000,
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        >
          <div style={{
            position: 'relative',
            backgroundColor: S.ink,
            color: S.paper,
            padding: '6px 12px',
            borderRadius: S.radius,
            fontSize: 13,
            fontWeight: 400,
            letterSpacing: '.04em',
            whiteSpace: 'nowrap',
            border: `1px solid ${S.ink}`,
          }}>
            <span style={{
              position: 'absolute',
              left: -7, top: '50%', transform: 'translateY(-50%)',
              width: 0, height: 0,
              borderTop: '7px solid transparent',
              borderBottom: '7px solid transparent',
              borderRight: `7px solid ${S.ink}`,
            }} />
            <span style={{
              position: 'absolute',
              left: -6, top: '50%', transform: 'translateY(-50%)',
              width: 0, height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: `6px solid ${S.ink}`,
            }} />
            {collapsedTooltip.text}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Desktop has no top header (collapse toggle + language live in the
            sidebar). Phone/tablet: menu button · page title · avatar menu. */}
        {isMobile && (
          <header className="main-header" style={{ minHeight: 56 }}>
            <div className="header-left" style={{ gap: 10 }}>
              <button
                className="btn btn-icon btn-secondary mobile-menu-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={isZh ? '打开菜单' : 'Open menu'}
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <div className="dl-mobile-title">{getPageTitle()}</div>
            </div>
            <div className="header-actions" ref={accountMenuRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="dl-account-btn"
                onClick={() => setAccountMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                aria-label={isZh ? '我的账户' : 'My account'}
              >
                {avatarCircle(34)}
              </button>
              {accountMenuOpen && (
                <div className="dl-account-menu" role="menu">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${S.line}` }}>
                    {avatarCircle(40)}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: S.serif, fontSize: 20, fontWeight: 500, color: S.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayFirstName}</div>
                      <div style={{ fontSize: 11, letterSpacing: '.18em', color: S.sub }}>{roleLabel}</div>
                    </div>
                  </div>
                  <NavLink to="/settings" className="dl-row" style={menuRowStyle} role="menuitem" onClick={() => setAccountMenuOpen(false)}>
                    <Settings size={18} strokeWidth={1.5} color={S.sub} />{t('nav.settings')}
                  </NavLink>
                  <div className="dl-row" style={{ ...menuRowStyle, cursor: 'default' }}>
                    <Globe size={18} strokeWidth={1.5} color={S.sub} />
                    <span style={{ flex: 1 }}>{isZh ? '语言' : 'Language'}</span>
                    {languageToggle(false)}
                  </div>
                  <button type="button" className="dl-row" style={{ ...menuRowStyle, opacity: clearingCache ? 0.6 : 1 }} role="menuitem"
                    onClick={handleClearCache} disabled={clearingCache}>
                    <Eraser size={18} strokeWidth={1.5} color={S.sub} />
                    {clearingCache ? (isZh ? '清除中…' : 'Clearing…') : t('nav.clearCache', isZh ? '清除缓存' : 'Clear cache')}
                  </button>
                  <button type="button" className="dl-row" style={{ ...menuRowStyle, color: S.bad, borderTop: `1px solid ${S.line}` }} role="menuitem"
                    onClick={() => { setAccountMenuOpen(false); handleLogout(); }}>
                    <LogOut size={18} strokeWidth={1.5} />{t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </header>
        )}

        <main className="main-body">
          <Outlet />
        </main>
      </div>

      {/* Phone bottom tab bar — the role's 4 most-used pages + 更多 (opens the drawer). */}
      {showTabBar && (
        <nav className="dl-tabbar" aria-label={isZh ? '常用页面' : 'Main pages'}>
          {tabItems.map((item) => {
            const Icon = item.icon;
            const badge = getBadgeCount(item.name);
            const [zh, en] = TAB_SHORT[item.path] || [navLabel(item.name, item.fallback), navLabel(item.name, item.fallback)];
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) => `dl-tab ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={21} />
                <span className="dl-tab-label">{isZh ? zh : en}</span>
                {badge > 0 && <span className="dl-tab-badge">{badge > 99 ? '99+' : badge}</span>}
              </NavLink>
            );
          })}
          <button
            type="button"
            className={`dl-tab ${!anyTabActive || sidebarOpen ? 'active' : ''}`}
            onClick={() => setSidebarOpen(true)}
            aria-label={isZh ? '更多菜单' : 'More'}
          >
            <Menu size={21} />
            <span className="dl-tab-label">{isZh ? '更多' : 'More'}</span>
          </button>
        </nav>
      )}
    </div>
  );
};

export default DashboardLayout;
